import {
    IAbvSelections,
    IAvailabilitySelections,
    IFilters,
    IIbuSelections,
    IRangeLabelObject,
    IResponse,
    IShowAllFilters,
    ISrmSelections,
} from '../types'

import {
    Availability,
    BrewTypes,
    CompositeProduct,
    InputGetAll,
    Style,
    StyleConnection,
} from 'src/types/schema/graphql'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { apiRequest, RequestInput } from 'src/api/apiRequest'
import { allStyles } from '../utils/queries'

interface IFilterStore {
    toggleStyleArrowUp: boolean
    setToggleStyleArrowUp: (toggleStyleArrowUp: boolean) => void
    getStyles: () => Promise<void>
    brewStyles: Style[]
    setBrewStyles: (brewStyles: IResponse['data']['allStyles']['items']) => void
    styleFilter: string[]
    setStyleFilter: (styleFilter: string[]) => void
    filters: IFilters
    setFilters: (filters: IFilters) => void
    page: number
    setPage: (page: number) => void
    nextResultsOffset: string
    setNextResultsOffset: (nextResultsOffset: string) => void
    brewTypeOptions: BrewTypes[]
    setBrewTypeOptions: (brewTypeOptions: BrewTypes[]) => void
    toggleBrewArrowUp: boolean
    setToggleBrewArrowUp: (toggleBrewArrowUp: boolean) => void
    showAllFilters: IShowAllFilters
    setShowAllFilters: (showAllFilters: IShowAllFilters) => void
    toggleAbvArrowUp: boolean
    setToggleAbvArrowUp: (toggleAbvArrowUp: boolean) => void
    abvFilter: IRangeLabelObject
    setAbvFilter: (abvFilter: IRangeLabelObject) => void
    toggleIbuArrowUp: boolean
    setToggleIbuArrowUp: (toggleIbuArrowUp: boolean) => void
    ibuFilter: IRangeLabelObject
    setIbuFilter: (ibuFilter: IRangeLabelObject) => void
    toggleSrmArrowUp: boolean
    setToggleSrmArrowUp: (toggleSrmArrowUp: boolean) => void
    srmFilter: IRangeLabelObject
    setSrmFilter: (srmFilter: IRangeLabelObject) => void
    toggleAvailabilityArrowUp: boolean
    setToggleAvailabilityArrowUp: (toggleAvailabilityArrowUp: boolean) => void
    availabilityFilter: Availability[]
    setAvailabilityFilter: (availabilityFilter: Availability[]) => void
    availabilitySelections: IAvailabilitySelections
    setAvailabilitySelections: (availabilitySelections: IAvailabilitySelections) => void
    ibuSelections: IIbuSelections
    setIbuSelections: (ibuSelections: IIbuSelections) => void
    abvSelections: IAbvSelections
    setAbvSelections: (abvSelections: IAbvSelections) => void
    srmSelections: ISrmSelections
    setSrmSelections: (srmSelections: ISrmSelections) => void
    toggleStateArrowUp: boolean
    setToggleStateArrowUp: (toggleStateArrowUp: boolean) => void
    stateFilter: string
    setStateFilter: (stateFilter: string) => void
    filterDrawerOpen: boolean
    setFilterDrawerOpen: (filterDrawerOpen: boolean) => void
    allBrewStyles: Style[]
    setAllBrewStyles: (allBrewStyles: Style[]) => void
    isResultsLoading: boolean
    setIsResultsLoading: (isResultsLoading: boolean) => void
    searchResults: CompositeProduct[]
    setSearchResults: (searchResults: CompositeProduct[]) => void
    totalResults: number
    setTotalResults: (totalResults: number) => void
}

export const useFilterStore = create<IFilterStore>()(
    devtools(
        (set, get) => ({
            toggleStyleArrowUp: true,
            setToggleStyleArrowUp: (toggleStyleArrowUp) => set({ toggleStyleArrowUp }),
            brewStyles: [],
            setBrewStyles: (brewStyles) => set({ brewStyles }),
            getStyles: async () => {
                const input: RequestInput<InputGetAll> = {
                    query: allStyles,
                    args: {
                        input: {
                            limit: 1000,
                        },
                    },
                    file: 'filterStore.ts',
                    endpoint: 'getStyles',
                    noAlerts: true,
                    publicQuery: true,
                }
                const { data } = await apiRequest<StyleConnection, InputGetAll>(input)

                if (data) get().setBrewStyles(data?.items)
            },
            styleFilter: [],
            setStyleFilter: (styleFilter) => set({ styleFilter }),
            filters: null,
            setFilters: (filters) => {
                set((state) => ({
                    filters: {
                        ...state.filters,
                        filters,
                    },
                }))
            },
            page: 1,
            setPage: (page) => set({ page }),
            nextResultsOffset: null,
            setNextResultsOffset: (nextResultsOffset) => set({ nextResultsOffset }),
            brewTypeOptions: [],
            setBrewTypeOptions: (brewTypeOptions) => set({ brewTypeOptions }),
            toggleBrewArrowUp: true,
            setToggleBrewArrowUp: (toggleBrewArrowUp) => set({ toggleBrewArrowUp }),
            showAllFilters: {
                brewOptions: false,
                flavorNotes: false,
                abv: false,
                ibu: false,
                srm: false,
                availability: false,
                state: false,
            },
            setShowAllFilters: (showAllFilters) => set({ showAllFilters }),
            toggleAbvArrowUp: true,
            setToggleAbvArrowUp: (toggleAbvArrowUp) => set({ toggleAbvArrowUp }),
            abvFilter: null,
            setAbvFilter: (abvFilter) => set({ abvFilter }),
            toggleIbuArrowUp: true,
            setToggleIbuArrowUp: (toggleIbuArrowUp) => set({ toggleIbuArrowUp }),
            ibuFilter: null,
            setIbuFilter: (ibuFilter) => set({ ibuFilter }),
            toggleSrmArrowUp: true,
            setToggleSrmArrowUp: (toggleSrmArrowUp) => set({ toggleSrmArrowUp }),
            srmFilter: null,
            setSrmFilter: (srmFilter) => set({ srmFilter }),
            toggleAvailabilityArrowUp: true,
            setToggleAvailabilityArrowUp: (toggleAvailabilityArrowUp) =>
                set({ toggleAvailabilityArrowUp }),
            availabilityFilter: [Availability.Limited, Availability.Seasonal, Availability.YearRound],
            setAvailabilityFilter: (availabilityFilter) => set({ availabilityFilter }),
            availabilitySelections: {
                Year_Round: false,
                Seasonal: false,
                Limited: false,
                Archived: false,
            },
            setAvailabilitySelections: (availabilitySelections) => set({ availabilitySelections }),
            ibuSelections: {
                'Low (<15)': false,
                'Light (16-25)': false,
                'Moderate (26-40)': false,
                'High (41-70)': false,
                'Extremely High (>71)': false,
            },
            setIbuSelections: (ibuSelections) => set({ ibuSelections }),
            abvSelections: {
                'Non-Alcoholic (<2%)': false,
                'Low (2-4.5%)': false,
                'Normal (4.5-6.5%)': false,
                'Elevated (6.5-8.5%)': false,
                'High (8.5-12%)': false,
                'Extremely High (>12%)': false,
            },
            setAbvSelections: (abvSelections) => set({ abvSelections }),
            srmSelections: {
                'Light (<5)': false,
                'Gold (6-9)': false,
                'Amber (10-18)': false,
                'Brown (19-29)': false,
                'Dark Brown (30-37)': false,
                'Black (>38)': false,
            },
            setSrmSelections: (srmSelections) => set({ srmSelections }),
            toggleStateArrowUp: true,
            setToggleStateArrowUp: (toggleStateArrowUp) => set({ toggleStateArrowUp }),
            stateFilter: null,
            setStateFilter: (stateFilter) => set({ stateFilter }),
            filterDrawerOpen: false,
            setFilterDrawerOpen: (filterDrawerOpen) => set({ filterDrawerOpen }),
            allBrewStyles: [],
            setAllBrewStyles: (allBrewStyles) => set({ allBrewStyles }),
            isResultsLoading: false,
            setIsResultsLoading: (isResultsLoading) => set({ isResultsLoading }),
            searchResults: [],
            setSearchResults: (searchResults) => set({ searchResults }),
            totalResults: null,
            setTotalResults: (totalResults) => set({ totalResults }),
        }),
        { name: 'BrewKnowledgeFilterStore' }
    )
)
